import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import StoryCircle from './StoryCircle';
import AddStoryCircle from './AddStoryCircle';
import StoryViewer from './StoryViewer';
import StoryCreator from './StoryCreator';
import store from '../storyStore';

/**
 * Horizontal scrollable bar of user story circles.
 * Displayed below the IndexPage hero section.
 *
 * Each user gets exactly one circle, however many stories they posted; the
 * viewer walks through that user's stories and then moves on to the next user.
 *
 * State:
 *   groups    – array of {user, isViewed, stories[]} from API
 *   canCreate – whether actor may create a story
 *   loading   – initial load in progress
 *   error     – load failed
 */
export default class StoryBar extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    // Only what belongs to this mounting of the bar. The stories themselves
    // live on the store, so returning from a discussion shows them at once
    // instead of clearing to a skeleton and refetching.
    this.viewerOpen   = false;
    this.creatorOpen  = false;
    this.viewerIndex  = 0; // which user group is open in the viewer

    this.loadStories();
  }

  /** The bar's data, shared across every visit to the index. */
  get groups() {
    return store.bar.groups;
  }

  get canCreate() {
    return store.bar.canCreate;
  }

  get error() {
    return store.bar.error;
  }

  /** A skeleton belongs to the very first load only, not to later returns. */
  get loading() {
    return !store.bar.loaded && !store.bar.error;
  }

  view() {
    // Placeholders only before anything has ever loaded. A return to the index
    // already holds the previous circles, so it keeps showing them while the
    // refetch runs rather than replacing them with a skeleton — swapping real
    // content for placeholders and back is the flicker, even at equal height.
    if (this.loading) {
      return (
        <div className="StoryBar">
          <div className="StoryBar-scroll">
            {[...Array(5)].map((_, i) => (
              <div className="StoryCircle-skeleton" key={i}>
                <div className="StoryCircle-skeleton-ring" />
                <div className="StoryCircle-skeleton-name" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Nothing to show: no bar at all, rather than an empty strip. The bar is
    // absent in the same way for a failed load, since a story bar is not worth
    // an error message of its own.
    if (this.error || (!this.canCreate && this.groups.length === 0)) {
      return <div />;
    }

    return (
      <div className="StoryBar">
        <div className="StoryBar-scroll">
          {/* Add story circle — canCreate already reflects the group limit */}
          {this.canCreate && (
            <AddStoryCircle onclick={() => this.openCreator()} />
          )}

          {/* Story circles — one per user */}
          {this.groups.map((group, idx) => (
            <StoryCircle
              key={group.user.id}
              group={group}
              onclick={() => this.openViewer(idx)}
            />
          ))}
        </div>

        {/* Story Viewer modal */}
        {this.viewerOpen && (
          <StoryViewer
            groups={this.groups}
            startGroup={this.viewerIndex}
            onclose={() => this.closeViewer()}
            onliketoggle={(g, i, result) => this.handleLikeToggle(g, i, result)}
            onviewed={(g, i, result) => this.handleViewed(g, i, result)}
            ondelete={(g, i) => this.handleDelete(g, i)}
          />
        )}

        {/* Story Creator modal */}
        {this.creatorOpen && (
          <StoryCreator
            onclose={() => this.closeCreator()}
            oncreated={(story, canCreate) => this.handleCreated(story, canCreate)}
          />
        )}
      </div>
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  openViewer(idx) {
    this.viewerIndex = idx;
    this.viewerOpen  = true;
    m.redraw();
  }

  closeViewer() {
    this.viewerOpen = false;
    m.redraw();
  }

  openCreator() {
    this.creatorOpen = true;
    m.redraw();
  }

  closeCreator() {
    this.creatorOpen = false;
    m.redraw();
  }

  // ── API ───────────────────────────────────────────────────────────────────

  /**
   * Ask the store for the bar's data, refetching only when it has none.
   *
   * Passing force refetches even when something is already cached, for after a
   * story is created or deleted.
   */
  loadStories(force) {
    store
      .loadBar(force)
      .then(() => m.redraw())
      .catch(() => m.redraw());
  }

  storyAt(groupIdx, storyIdx) {
    const group = this.groups[groupIdx];
    return group && group.stories[storyIdx];
  }

  handleLikeToggle(groupIdx, storyIdx, result) {
    const story = this.storyAt(groupIdx, storyIdx);
    if (story) {
      story.isLiked    = result.isLiked;
      // Counts come back only for the owner; absent they must not wipe what
      // the story already carries.
      if (result.likesCount !== undefined) story.likesCount = result.likesCount;
      // Trusted from the server rather than adjusted here: someone else may
      // have reacted while this request was in flight.
      story.myReaction = result.myReaction;
      // Only the owner is sent a breakdown; for anyone else the field is
      // absent and must not wipe what the story already carries.
      if (result.reactions) story.reactions = result.reactions;
      m.redraw();
    }
  }

  handleViewed(groupIdx, storyIdx, result) {
    const story = this.storyAt(groupIdx, storyIdx);
    if (!story) return;

    story.isViewed   = true;
    story.viewsCount = result.viewsCount;

    // The ring only goes grey once every story of that user has been seen.
    const group = this.groups[groupIdx];
    group.isViewed = group.stories.every((s) => s.isViewed);

    // Keep that user's rings and post badges elsewhere on the page in step.
    // Deferred: this runs inside a request callback, and a redraw asked for
    // while one is already under way is dropped — which left the badge showing
    // its old state until some unrelated repaint.
    if (group.isViewed) {
      store.markSeen(group.user.id);
      setTimeout(() => m.redraw(), 0);
    }

    m.redraw();
  }

  handleDelete(groupIdx, storyIdx) {
    const group = this.groups[groupIdx];
    if (!group) return;

    group.stories.splice(storyIdx, 1);

    // Deleting frees a slot, so the add button may be available again.
    // Only for the actor's own stories — deleting someone else's (as an
    // admin) doesn't change what the actor is allowed to post.
    const userId = app.session.user && Number(app.session.user.id());
    if (Number(group.user.id) === userId) store.bar.canCreate = true;

    // Drop the whole circle once its last story is gone.
    if (group.stories.length === 0) {
      this.groups.splice(groupIdx, 1);
      this.viewerOpen = false;

      // That user's avatar rings must disappear too.
      store.unmarkAuthor(group.user.id);
    }

    m.redraw();
  }

  handleCreated(story, canCreate) {
    // The server reports whether the limit leaves room for another one.
    if (canCreate !== undefined) store.bar.canCreate = !!canCreate;

    // Flarum's Model.id() yields a string while the API sends a number, so
    // these have to be compared numerically — a strict match never held and
    // every new story opened a second circle of its own.
    const userId = app.session.user && Number(app.session.user.id());
    const existing = this.groups.find((g) => Number(g.user.id) === userId);

    // Append to the actor's own circle rather than adding a second one.
    if (existing) {
      existing.stories.push(story);
      existing.isViewed = false;
    } else {
      this.groups.unshift({ user: story.user, isViewed: false, stories: [story] });
    }

    // The author now needs a ring on their avatars everywhere.
    if (userId) store.markAuthor(userId);

    this.creatorOpen = false;
    m.redraw();
  }
}
